/* eslint-disable rover/prefer-kibble-components */
import React from 'react';
import { plural, t } from '@lingui/macro';

import Avatar, { AvatarType, Size } from '@rover/components/src/Avatar/Avatar';
import CalloutBadge from '@rover/components/src/callouts/CalloutBadge/CalloutBadge';
import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import StarRating from '@rover/kibble/patterns/StarRating/StarRating';
import Elevator from '@rover/react-lib/src/components/utils/Elevator/Elevator';
import { Logger } from '@rover/rsdk/src/modules/Logging';

type SitterInfo = {
  shortName: string;
  reviewsCount: number;
  ratingsAverage: string;
  webUrl: string;
  picture: string;
  location: string;
  isStarSitter?: boolean;
};

type Props = {
  sitter: SitterInfo;
};

const MoreSittersCard = ({ sitter }: Props): JSX.Element => {
  const elevatorStyles = {
    style: { borderRadius: '8px' },
  };
  const anchorStyles = {
    style: { textDecoration: 'none', color: 'inherit', cursor: 'pointer' },
  };

  // texts for accessibility
  const rating = sitter.ratingsAverage;
  const maxRating = 5;
  const ratingText = t`Rating: ${rating} out of ${maxRating} stars`;
  const reviewsText = plural(sitter.reviewsCount, {
    one: `Review`,
    other: `Reviews`,
  });

  return (
    <Elevator elevation={1} {...elevatorStyles}>
      <a
        data-testid="anchor"
        href={sitter.webUrl}
        onClick={() => Logger.info('sitter_profile_more_sitters_clicked')}
        {...anchorStyles}
        aria-label={`${sitter.shortName}, ${sitter.location}, ${ratingText}, ${sitter.reviewsCount} ${reviewsText}`}
      >
        <Flex p="4x" aria-hidden="true">
          <Box mr="4x">
            <Avatar
              imageUrl={sitter.picture}
              avatarType={AvatarType.Person}
              size={Size.Small}
              borderRadius="50%"
              alt={`${sitter.shortName}'s profile picture`}
            />
          </Box>
          <Flex flexDirection="column" justifyContent="center" alignItems="start" flex="1 1 0">
            <Flex alignItems="center" alignSelf="stretch" gap="4x" minHeight="27px">
              <Flex alignItems="start" gap="1x" flex="1 0 0">
                <Heading size="200" mb="1x">
                  {sitter.shortName}
                </Heading>
              </Flex>
              {sitter.isStarSitter && (
                <CalloutBadge
                  value={t`Star Sitter`}
                  calloutBadgeStyle={CalloutBadge.Styles.STAR_SITTER_SMALL}
                />
              )}
            </Flex>
            <Text size="100" mb="2x">
              {sitter.location}
            </Text>
            <Flex alignItems="center" mt="1x">
              {parseFloat(sitter.ratingsAverage) > 0 && (
                <StarRating
                  rating={parseFloat(sitter.ratingsAverage)}
                  variant="compact"
                  textSize="100"
                />
              )}
              <Text size="100" ml={parseFloat(sitter.ratingsAverage) > 0 ? '1x' : '0x'}>
                {`${parseFloat(sitter.ratingsAverage) > 0 ? '• ' : ''}${
                  sitter.reviewsCount
                } ${reviewsText}`}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </a>
    </Elevator>
  );
};

export default MoreSittersCard;
